import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Trash2, X, Plus } from "lucide-react";
import { api, errMsg, getToken, money } from "@/lib/api";
import { EmptyState } from "@/components/Bits";
import HigherAssignForm from "@/components/admin/HigherAssignForm";
import HigherJoinerFormDialog from "@/components/admin/HigherJoinerFormDialog";
import HigherMemberClaimDialog from "@/components/admin/HigherMemberClaimDialog";
import HigherEmsPayment from "@/components/HigherEmsPayment";
import HigherSummaryBox from "@/components/HigherSummaryBox";
import HigherDomsForm from "@/components/HigherDomsForm";
import HigherDomsAdmin from "@/components/admin/HigherDomsAdmin";
import HigherShipTracker from "@/components/HigherShipTracker";

const ClaimRow = ({ c, isAdmin, onRemove }) => (
  <div data-testid={`higher-claim-row-${c.id}`} className="flex items-center gap-3 border-t border-white/5 py-2.5 first:border-t-0">
    <span className="min-w-0 flex-1 truncate">{c.set_name}</span>
    <span className="font-mono text-sm text-zinc-300">{c.pcs} PC{c.pcs === 1 ? "" : "s"}</span>
    {isAdmin && (
      <button type="button" data-testid={`higher-claim-remove-${c.id}`} onClick={() => onRemove(c)} className="rounded-lg p-1 text-zinc-500 transition-colors hover:bg-red-500/10 hover:text-red-300"><X className="h-4 w-4" /></button>
    )}
  </div>
);

const MemberRow = ({ c, isAdmin, onRemoveMember }) => (
  <div data-testid={`higher-member-claim-row-${c.id}`} className="flex items-center gap-3 border-t border-white/5 py-2.5 first:border-t-0">
    <div className="min-w-0 flex-1">
      <div className="truncate"><span className="text-zinc-400">{c.set_name} ·</span> {c.members.join(", ")}</div>
      {c.missing.length > 0 && <div data-testid={`higher-member-claim-missing-${c.id}`} className="text-xs text-red-300">Missing: {c.missing.join(", ")} ({c.missing_pcs} PC{c.missing_pcs === 1 ? "" : "s"})</div>}
    </div>
    <span className="font-mono text-sm text-zinc-300">{c.pcs} PC{c.pcs === 1 ? "" : "s"}</span>
    {isAdmin && (
      <button type="button" data-testid={`higher-member-claim-remove-${c.id}`} onClick={() => onRemoveMember(c)} className="rounded-lg p-1 text-zinc-500 transition-colors hover:bg-red-500/10 hover:text-red-300"><X className="h-4 w-4" /></button>
    )}
  </div>
);

const DropCard = ({ d, ...rest }) => (
  <div data-testid={`higher-joiner-drop-${d.id}`} className="rise rounded-2xl border border-white/10 bg-zinc-900/60 p-4 sm:p-5">
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="font-display font-bold text-lg leading-tight">{d.name}</div>
        <div className="text-xs text-zinc-500 mt-0.5">{(d.sets || []).map((s) => `${s.name} ×${s.count}`).join(" · ")}{d.missing_items ? ` · ${d.missing_sets} missing set${d.missing_sets === 1 ? "" : "s"}` : ""}</div>
      </div>
      <div className="text-right">
        <div data-testid={`higher-drop-ems-owed-${d.id}`} className="font-mono font-bold text-sky-300">{d.has_ems ? money(d.ems_owed) : "No EMS"}</div>
        {d.has_ems && <div className="text-[11px] text-zinc-500">{money(d.ems)} × {d.pcs - d.missing_pcs} PCs{d.missing_pcs ? ` (${d.missing_pcs} missing)` : ""}</div>}
      </div>
    </div>
    <div className="mt-3">
      {d.member_claims.map((c) => <MemberRow key={c.id} c={c} {...rest} />)}
      {d.claims.map((c) => <ClaimRow key={c.id} c={c} {...rest} />)}
    </div>
  </div>
);

export default function HigherJoinerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isAdmin = !!getToken();
  const [j, setJ] = useState(null);
  const [missing, setMissing] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [adding, setAdding] = useState(false);
  const load = useCallback(() => {
    api.get(`/higher/joiners/${id}`).then((r) => setJ(r.data)).catch(() => setMissing(true));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    try { await fn(); toast.success(ok); return true; } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const remove = (c) => window.confirm(`Remove ${c.pcs} PC(s) of ${c.set_name}?`) && act(() => api.delete(`/higher/claims/${c.id}`), "PCs removed").then(load);
  const removeMember = (c) => window.confirm(`Remove ${c.members.join(", ")} from ${c.set_name}?`) && act(() => api.delete(`/higher/member-claims/${c.id}`), "Claim removed").then(load);
  const del = async () => {
    if (window.confirm(`Delete ${j.name} from Higher Joiners? Their PCs and EMS payments will be removed.`) && (await act(() => api.delete(`/higher/joiners/${id}`), "Higher Joiner deleted"))) navigate("/higher");
  };

  if (missing) return (
    <div>
      <Link to="/higher" data-testid="back-to-higher" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors mb-4"><ArrowLeft className="h-4 w-4" /> Higher Store Claims</Link>
      <EmptyState testId="higher-joiner-missing" title="Higher Joiner not found" hint="Go back to Higher Store Claims and pick your name." />
    </div>
  );
  if (!j) return <div className="text-zinc-500 text-sm">Loading…</div>;

  return (
    <div className="rise">
      <Link to="/higher" data-testid="back-to-higher" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors"><ArrowLeft className="h-4 w-4" /> Higher Store Claims</Link>
      <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
        <h1 data-testid="higher-joiner-name" className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight break-words">{j.name}</h1>
        {isAdmin && (
          <div className="flex gap-1">
            <button type="button" data-testid="higher-joiner-rename" onClick={() => setRenaming(true)} className="rounded-lg p-2 text-zinc-400 transition-colors hover:bg-white/5 hover:text-white"><Pencil className="h-4 w-4" /></button>
            <button type="button" data-testid="higher-joiner-delete" onClick={del} className="rounded-lg p-2 text-zinc-400 transition-colors hover:bg-red-500/10 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
          </div>
        )}
      </div>
      <HigherSummaryBox j={j} isAdmin={isAdmin} onChanged={load} />
      <HigherShipTracker j={j} isAdmin={isAdmin} onChanged={load} />
      {isAdmin && (
        <button type="button" data-testid="higher-add-drop-claim-btn" onClick={() => setAdding(true)}
          className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-sm font-semibold text-zinc-950 transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-zinc-200">
          <Plus className="h-4 w-4" /> Higher Drop
        </button>
      )}
      {isAdmin && <HigherAssignForm joinerId={id} onAdded={load} />}
      <HigherEmsPayment j={j} onChanged={load} />
      <HigherDomsForm j={j} onChanged={load} />
      {isAdmin && <HigherDomsAdmin joinerId={id} refreshKey={j.doms?.submitted_at} onChanged={load} />}
      <div className="mt-8 space-y-3" data-testid="higher-joiner-drops">
        {j.drops.length === 0 && <EmptyState testId="higher-joiner-empty" title="No Higher PCs yet" hint="PCs show here once an admin assigns them." />}
        {j.drops.map((d) => <DropCard key={d.id} d={d} isAdmin={isAdmin} onRemove={remove} onRemoveMember={removeMember} />)}
      </div>
      {adding && <HigherMemberClaimDialog joinerId={id} onClose={() => setAdding(false)} onSaved={load} />}
      {renaming && <HigherJoinerFormDialog joiner={j} onClose={() => setRenaming(false)} onSaved={load} />}
    </div>
  );
}
